import React from "react";
import { Link, useLocation } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import DynamicTitle from "./DynamicTitle";
import WaveDivider from "./WaveDivider";
import Accordion from "./Accordion";
import LoopsPopup from "./LoopsPopup";
import { useAuth } from "../contexts/AuthContext";

function Home() {
  const location = useLocation();
  const { isSubscribed } = useAuth();
  return (
    <div className="flex flex-col min-h-screen">
      <DynamicTitle>Home</DynamicTitle>
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-hero-pattern bg-cover bg-center relative pb-0">
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="max-w-screen-xl mx-auto px-4 text-center relative z-10 py-20">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              Find Calm Through Bilateral Stimulation
            </h1>
            <p className="text-lg sm:text-xl mb-8 text-white">
              A simple way to settle your mind and reset your day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="inline-block bg-accent hover:bg-accentDark text-white font-semibold py-3 px-6 rounded-full transition-colors"
              >
                Try Interactive Demo
              </Link>
              <Link
                to="/subscribe"
                className="inline-block bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-semibold py-3 px-6 rounded-full transition-colors border border-white border-opacity-30"
              >
                Start Your Free Trial
              </Link>
            </div>
            <p className="text-sm text-white text-opacity-90 mt-4">
              No signup required • You're always in control — pause, adjust, or stop anytime
            </p>
          </div>
          {/* Wave: hero → warm white */}
          <div className="relative z-10">
            <WaveDivider topColor="transparent" bottomColor="background" />
          </div>
        </section>

        {/* Introduction to Bilateral Stimulation */}
        <section className="pt-4 pb-12 bg-background" style={{ marginTop: "-1px" }}>
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-text">What is Bilateral Stimulation?</h2>
            <p className="text-base mb-4 text-textSecondary">
              Bilateral stimulation is any sensory input that alternates between the left and right
              sides of your body—like following a moving dot with your eyes or hearing tones that
              switch between ears. Research shows this rhythmic left-right pattern can help take the
              edge off stress and bring you back to a calmer baseline.
            </p>
            <p className="text-base mb-4 text-textSecondary">
              Our sessions guide you through it in a simple, self-directed way—helping you find calm
              and clarity at your own pace.
            </p>
            <Link
              to="/what-is-bilateral-stimulation"
              className="text-accent hover:underline font-medium"
            >
              Learn more about how it works &rarr;
            </Link>

            {/* Privacy Trust Badge */}
            <div className="mt-8 pt-6 border-t border-tint">
              <div className="flex flex-wrap justify-center gap-6 text-sm text-textSecondary">
                <span className="flex items-center gap-2">
                  <span className="text-secondary">🔒</span> Privacy-first: Your data stays private
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-secondary">✓</span> No ads, no tracking
                </span>
                <Link to="/privacy-policy" className="text-accent hover:underline">
                  Learn more
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Wave: warm white → oat */}
        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* Features & Benefits */}
        <section className="bg-surfaceTint pt-8 pb-12">
          <div className="max-w-screen-xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8 text-text">
              Why Choose Realign Mind?
            </h2>
            <div className="flex flex-wrap -mx-4">
              {/* Feature 1 */}
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="text-center">
                  <img
                    src="/images/healfromtrauma.png"
                    alt="Find calm and clarity"
                    className="mx-auto mb-4 w-20 h-20"
                  />
                  <h3 className="text-xl font-semibold mb-2 text-text">
                    Find Calm in Hard Moments
                  </h3>
                  <p className="text-base text-textSecondary">
                    Take the edge off a tough day and come back to yourself.
                  </p>
                </div>
              </div>
              {/* Feature 2 */}
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="text-center">
                  <img
                    src="/images/scriptedsessions.webp"
                    alt="Scripted Sessions"
                    className="mx-auto mb-4 w-20 h-20"
                  />
                  <h3 className="text-xl font-semibold mb-2 text-text">
                    Guided Sessions
                  </h3>
                  <p className="text-base text-textSecondary">
                    Access sessions created by experienced professionals.
                  </p>
                </div>
              </div>
              {/* Feature 3 */}
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="text-center">
                  <img
                    src="/images/flexibleaccess.webp"
                    alt="Flexible Access"
                    className="mx-auto mb-4 w-20 h-20"
                  />
                  <h3 className="text-xl font-semibold mb-2 text-text">
                    Flexible Access
                  </h3>
                  <p className="text-base text-textSecondary">
                    Engage in sessions anytime, anywhere.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Wave: oat → warm white */}
        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Who It's For */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-4 text-text">
              Is RealignMind Right for You?
            </h2>
            <p className="text-center text-textSecondary mb-8 max-w-2xl mx-auto">
              RealignMind is a self-directed wellness tool. It's designed for people who want to
              add bilateral stimulation to their personal wellness routine.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-lg border border-tint">
                <div className="text-2xl mb-3">🧘</div>
                <h3 className="font-semibold text-text mb-2">Seeking Calm</h3>
                <p className="text-sm text-textSecondary">
                  You're looking for a way to unwind and find moments of peace in a busy life.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-tint">
                <div className="text-2xl mb-3">🔄</div>
                <h3 className="font-semibold text-text mb-2">Supplementing Self-Care</h3>
                <p className="text-sm text-textSecondary">
                  You want to add a research-backed technique to your existing wellness practices.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-tint">
                <div className="text-2xl mb-3">🏠</div>
                <h3 className="font-semibold text-text mb-2">Prefer Private Practice</h3>
                <p className="text-sm text-textSecondary">
                  You value the ability to practice on your own time, in your own space.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-tint">
                <div className="text-2xl mb-3">📱</div>
                <h3 className="font-semibold text-text mb-2">Want Accessibility</h3>
                <p className="text-sm text-textSecondary">
                  You appreciate having wellness tools available whenever you need them.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-tint">
                <div className="text-2xl mb-3">🌱</div>
                <h3 className="font-semibold text-text mb-2">Exploring New Approaches</h3>
                <p className="text-sm text-textSecondary">
                  You're curious about bilateral stimulation and want to experience it yourself.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-tint">
                <div className="text-2xl mb-3">💰</div>
                <h3 className="font-semibold text-text mb-2">Budget-Conscious</h3>
                <p className="text-sm text-textSecondary">
                  You want an affordable option for regular bilateral stimulation practice.
                </p>
              </div>
            </div>
            <p className="text-center text-sm text-textSecondary mt-8 max-w-2xl mx-auto">
              <strong>Important:</strong> RealignMind is a wellness tool, not a replacement for professional
              mental health care. If you're experiencing a mental health crisis, please visit our{" "}
              <Link to="/emergency-support" className="text-accent hover:underline">
                emergency support page
              </Link>{" "}
              or contact a healthcare provider.
            </p>
          </div>
        </section>

        {/* Wave: warm white → oat */}
        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* How It Works */}
        <section className="bg-surfaceTint pt-8 pb-12">
          <div className="max-w-screen-xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8 text-text">
              How It Works
            </h2>
            <div className="flex flex-wrap -mx-4">
              {/* Step 1 */}
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-accent mb-4">1</div>
                  <h3 className="text-xl font-semibold mb-2 text-text">Sign Up</h3>
                  <p className="text-base text-textSecondary">
                    Create your account and choose a subscription plan.
                  </p>
                </div>
              </div>
              {/* Step 2 */}
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-accent mb-4">2</div>
                  <h3 className="text-xl font-semibold mb-2 text-text">
                    Select a Session
                  </h3>
                  <p className="text-base text-textSecondary">
                    Choose from guided or unguided bilateral stimulation sessions.
                  </p>
                </div>
              </div>
              {/* Step 3 */}
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-accent mb-4">3</div>
                  <h3 className="text-xl font-semibold mb-2 text-text">Begin Your Journey</h3>
                  <p className="text-base text-textSecondary">
                    Start your journey towards mental wellness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Wave: oat → warm white */}
        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Quick FAQ Section */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8 text-text">
              Common Questions
            </h2>
            <div className="space-y-0">
              <Accordion title="Is bilateral stimulation safe?">
                <p className="text-textSecondary">
                  Yes, bilateral stimulation is generally considered safe for most people. It's a gentle,
                  non-invasive technique. However, it's not recommended during an active mental health crisis,
                  and it's not a replacement for professional care. If you have concerns, consult with a
                  healthcare provider.
                </p>
              </Accordion>
              <Accordion title="How long is a session?">
                <p className="text-textSecondary">
                  Sessions typically range from 5-20 minutes, depending on your preference. You can start
                  with shorter sessions and work up to longer ones as you become more comfortable. You're
                  always in control of when to stop.
                </p>
              </Accordion>
              <Accordion title="Do I need therapy experience?">
                <p className="text-textSecondary">
                  No prior experience is needed. Our sessions are designed to be intuitive and self-guided.
                  If you've worked with a therapist who uses EMDR, this will feel familiar—but it's also
                  accessible to complete beginners.
                </p>
              </Accordion>
              <Accordion title="Can I stop anytime?">
                <p className="text-textSecondary">
                  Absolutely. You're always in control. Every session has pause and stop controls readily
                  available. If something doesn't feel right, you can end your session immediately—no
                  questions asked.
                </p>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Wave: warm white → oat */}
        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* Additional Call to Action */}
        <section className="pt-8 pb-12 bg-surfaceTint">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-text">
              Ready to Try It?
            </h2>
            <p className="text-base mb-8 text-textSecondary">
              Experience bilateral stimulation for yourself—no signup required for the demo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="inline-block bg-accent hover:bg-accentDark text-white font-semibold py-3 px-6 rounded-full transition-colors"
              >
                Try Interactive Demo
              </Link>
              <Link
                to="/subscribe"
                className="inline-block bg-secondary hover:bg-[#6B304F] text-white font-semibold py-3 px-6 rounded-full transition-colors"
              >
                Start Your Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* Email Series CTA — hidden for paying subscribers */}
        {!isSubscribed && (
          <section className="py-12 bg-background">
            <div className="max-w-screen-sm mx-auto px-4 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
                Free Email Series
              </p>
              <h2 className="text-2xl font-bold mb-3 text-text">
                Not ready to commit? Start here.
              </h2>
              <p className="text-base text-textSecondary mb-6">
                Seven emails on the science behind bilateral stimulation. What the research says,
                what a session actually feels like, and how to know if it's right for you.
                No pressure, no pitch — just the information.
              </p>
              <LoopsPopup
                buttonText="Send me the free series"
                className="px-8 py-3 bg-accent hover:bg-accentDark text-white font-semibold rounded-full transition-colors"
                source="home-email-series"
              />
              <p className="text-xs text-textSecondary mt-3">Unsubscribe anytime.</p>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default Home;
